import React, { useCallback, useEffect, useState } from "react";
import { useField, useForm } from "react-final-form";
import { CheckCircle, CheckCircleOutline } from "@mui/icons-material";
import { CircularProgress, Typography } from "@mui/material";
import { Box } from "@mui/system";

import { Table, TableBody, TableCell, TableHead, TableRow } from "ui";

import analytics from "fond/analytics";
import { loadJson } from "fond/fileValidation";
import { Dropzone } from "fond/widgets";

import { c2sFileMap, getC2sFileSections } from "./files";
import { C2SArtifactFileType, matchC2SArtifactFile, MULTI_MATCH_ARTIFACT_TYPES, resolveLayerConfigurationInputLayers } from "./helper";
import { FormData } from "./SetupForm";
import { UploadFileMappingModal } from "./UploadFileMappingModal";

const FILE_ERROR_ERROR_MESSAGE = "Invalid file, please try another file.";

// Limit the number of displayed file names for artifacts that expect multiple uploads(e.g. splice tables)
const MAX_DISPLAYED_FILE_NAMES = 10;

/**
 * Formats a list of uploaded file names for display, listing at most MAX_DISPLAYED_FILE_NAMES names and
 * summarizing any remainder as a count (e.g. "a.csv, b.csv and 12 more").
 */
const formatUploadedNames = (names: string[]): string => {
  if (names.length <= MAX_DISPLAYED_FILE_NAMES) {
    return names.join(", ");
  }
  const remaining = names.length - MAX_DISPLAYED_FILE_NAMES;
  return `${names.slice(0, MAX_DISPLAYED_FILE_NAMES).join(", ")} and ${remaining} more`;
};

/**
 * Resolves the artifact type an uploaded file should be matched to, preferring a manual mapping (set via the
 * mapping modal) over fuzzy filename matching.
 */
const resolveFileType = (fileName: string, manualMappings: Record<string, C2SArtifactFileType>): C2SArtifactFileType | null => {
  return manualMappings[fileName] ?? matchC2SArtifactFile(fileName)?.id ?? null;
};

/**
 * Groups files by their matched artifact type, using fuzzy matching and manual mappings.
 */
const groupFilesByType = (files: File[], manualMappings: Record<string, C2SArtifactFileType>): Map<C2SArtifactFileType, File[]> => {
  const matchedFiles = new Map<C2SArtifactFileType, File[]>();
  files.forEach((file) => {
    const type = resolveFileType(file.name, manualMappings);
    if (type) {
      const typeFiles = matchedFiles.get(type) ?? [];
      typeFiles.push(file);
      matchedFiles.set(type, typeFiles);
    }
  });
  return matchedFiles;
};

/**
 * Adds new files of a specific artifact type to the existing file map, replacing any existing files of the same type
 * if the type does not support multiple matches.
 */
const addFilesOfType = (
  existingFileMap: Map<string, File>,
  fileType: C2SArtifactFileType,
  newFiles: File[],
  manualMappings: Record<string, C2SArtifactFileType>
): void => {
  if (!MULTI_MATCH_ARTIFACT_TYPES.has(fileType)) {
    for (const fileName of existingFileMap.keys()) {
      if (resolveFileType(fileName, manualMappings) === fileType) {
        existingFileMap.delete(fileName);
      }
    }
  }
  newFiles.forEach((file) => existingFileMap.set(file.name, file));
};

const UploadConfigurationStep: React.FC<{
  isReadOnly?: boolean;
  versionId: string;
  hasExistingSpliceTables: boolean;
  isSpliceTablesLoading: boolean;
  isSpliceViewEnabled: boolean;
}> = ({ isReadOnly, versionId, hasExistingSpliceTables, isSpliceTablesLoading, isSpliceViewEnabled }) => {
  const { change } = useForm();
  const {
    input: { value: files },
  } = useField<File[]>("Files.Artifacts");

  const {
    input: { value: layersConfiguration },
    meta: { error: layersStepError, touched: layersTouched },
  } = useField<FormData["CoreConfigInput"]["LayersConfiguration"]>("CoreConfigInput.LayersConfiguration", {
    validate: (value) => {
      if (!value?.Data) {
        return "Layers Configuration is required";
      }
      return undefined;
    },
  });

  const {
    input: { value: scenarioConfigInput },
    meta: { error: costToServeStepError, touched: costToServeTouched },
  } = useField<FormData["ScenarioConfigInput"]>("ScenarioConfigInput", {
    validate: (value) => {
      if (isSpliceViewEnabled) return undefined;
      const missing: string[] = [];
      if (!value?.BillOfMaterials?.Data) missing.push("bill of materials");
      if (!value?.CostBreakdown?.Data) missing.push("cost breakdown");
      if (missing.length > 0) {
        return `${missing.join(" and ")} ${missing.length > 1 ? "are" : "is"} required`;
      }
      return undefined;
    },
  });

  const {
    input: { value: spliceTables },
  } = useField<FormData["CoreConfigInput"]["SpliceTables"]>("CoreConfigInput.SpliceTables");

  const sectionErrors: Record<string, { error?: string; touched?: boolean }> = {
    required: { error: layersStepError, touched: layersTouched },
    costToServe: { error: costToServeStepError, touched: costToServeTouched },
  };

  // The Splice view file section is only shown when the collaboration_splice feature flag is on.
  const c2sFileSections = getC2sFileSections(isSpliceViewEnabled);
  const visibleFileSections = isSpliceViewEnabled ? c2sFileSections : c2sFileSections.filter((section) => !section.ids.includes("spliceTables"));

  const [fileErrors, setFileErrors] = useState<Partial<Record<C2SArtifactFileType, string>>>({});
  const [uploadedFileNames, setUploadedFileNames] = useState<Partial<Record<C2SArtifactFileType, string[]>>>({});
  const [unmatchedFiles, setUnmatchedFiles] = useState<File[]>([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [fileMapping, setFileMapping] = useState<Record<string, C2SArtifactFileType | "">>({});
  // Track manual mappings that persist after modal is closed
  const [manualMappings, setManualMappings] = useState<Record<string, C2SArtifactFileType>>({});

  const clearFileError = useCallback((type: C2SArtifactFileType) => {
    setFileErrors((prev) => {
      const next = { ...prev };
      delete next[type];
      return next;
    });
  }, []);

  // Process files when uploaded, using fuzzy matching and manual mappings
  useEffect(() => {
    const processFiles = async () => {
      const matchedFiles = groupFilesByType(files, manualMappings);

      // Update the uploaded file names state
      const fileNameMap: Partial<Record<C2SArtifactFileType, string[]>> = {};
      matchedFiles.forEach((typeFiles, type) => {
        fileNameMap[type] = typeFiles.map((file) => file.name);
      });
      setUploadedFileNames(fileNameMap);

      // Process layers configuration file (single file - the most recently uploaded one is used)
      const layersConfigFile = matchedFiles.get("layersConfiguration")?.slice(-1)[0];
      if (layersConfigFile) {
        try {
          const content = await loadJson(layersConfigFile);
          resolveLayerConfigurationInputLayers(content);
          change("CoreConfigInput.LayersConfiguration.Data", content);
          clearFileError("layersConfiguration");
        } catch (e) {
          change("CoreConfigInput.LayersConfiguration.Data", null);
          setFileErrors((prev) => ({ ...prev, layersConfiguration: FILE_ERROR_ERROR_MESSAGE }));
          analytics.track("collaboration.cts.files.validation.failed", {
            versionId,
            fileType: "layersConfiguration",
            error: FILE_ERROR_ERROR_MESSAGE,
          });
        }
      }

      // Process cost breakdown file (single file - the most recently uploaded one is used)
      const costBreakdownFile = matchedFiles.get("costBreakdown")?.slice(-1)[0];
      if (costBreakdownFile) {
        try {
          const value = await costBreakdownFile.text();
          change("ScenarioConfigInput.CostBreakdown.Data", value);
          clearFileError("costBreakdown");
        } catch (e) {
          change("ScenarioConfigInput.CostBreakdown.Data", null);
          setFileErrors((prev) => ({ ...prev, costBreakdown: FILE_ERROR_ERROR_MESSAGE }));
          analytics.track("collaboration.cts.files.validation.failed", {
            versionId,
            fileType: "costBreakdown",
            error: FILE_ERROR_ERROR_MESSAGE,
          });
        }
      }

      // Process bill of materials file (single file - the most recently uploaded one is used)
      const bomFile = matchedFiles.get("bom")?.slice(-1)[0];
      if (bomFile) {
        try {
          const value = await bomFile.text();
          change("ScenarioConfigInput.BillOfMaterials.Data", value);
          clearFileError("bom");
        } catch (e) {
          change("ScenarioConfigInput.BillOfMaterials.Data", null);
          setFileErrors((prev) => ({ ...prev, bom: FILE_ERROR_ERROR_MESSAGE }));
          analytics.track("collaboration.cts.files.validation.failed", {
            versionId,
            fileType: "bom",
            error: FILE_ERROR_ERROR_MESSAGE,
          });
        }
      }

      // Process splice table files (multiple files allowed - every matched file is kept).
      const spliceTableFiles = isSpliceViewEnabled ? (matchedFiles.get("spliceTables") ?? []) : [];
      if (spliceTableFiles.length > 0) {
        try {
          const tables = await Promise.all(spliceTableFiles.map(async (file) => ({ Type: "file" as const, Data: await file.text() })));
          change("CoreConfigInput.SpliceTables", tables);
          clearFileError("spliceTables");
        } catch (e) {
          change("CoreConfigInput.SpliceTables", []);
          setFileErrors((prev) => ({ ...prev, spliceTables: FILE_ERROR_ERROR_MESSAGE }));
          analytics.track("collaboration.cts.files.validation.failed", {
            versionId,
            fileType: "spliceTables",
            error: FILE_ERROR_ERROR_MESSAGE,
          });
        }
      } else {
        change("CoreConfigInput.SpliceTables", []);
      }
    };

    processFiles();
  }, [change, clearFileError, files, manualMappings, versionId, isSpliceViewEnabled]);

  const handleFilesChange = useCallback(
    async (newFiles: File[]) => {
      const matchedFiles = groupFilesByType(newFiles, manualMappings);
      const unmatched = newFiles.filter((file) => !resolveFileType(file.name, manualMappings));

      // Create a map of existing files by name for quick lookup
      const existingFileMap = new Map(files.map((file) => [file.name, file]));

      matchedFiles.forEach((typeFiles, fileType) => addFilesOfType(existingFileMap, fileType, typeFiles, manualMappings));

      // Track file upload (only if not in readonly mode)
      if (!isReadOnly) {
        analytics.track("collaboration.cts.files.uploaded", {
          versionId,
          fileCount: newFiles.length,
          fileTypes: newFiles.map((f) => f.name.split(".").pop() ?? ""),
          automaticMatches: matchedFiles.size,
          unmatchedFiles: unmatched.length,
        });

        // Track auto-matched files
        matchedFiles.forEach((_, matchType) => {
          analytics.track("collaboration.cts.files.auto_matched", {
            versionId,
            matchedType: matchType,
          });
        });
      }

      // Store matched files in the form state
      change("Files.Artifacts", Array.from(existingFileMap.values()));

      // If there are unmatched files, show the mapping modal
      if (unmatched.length > 0) {
        if (!isReadOnly) {
          analytics.track("collaboration.cts.files.manual_mapping.opened", {
            versionId,
            unmatchedCount: unmatched.length,
          });
        }
        setUnmatchedFiles(unmatched);
        // Initialize file mapping with auto-selected values where possible
        const initialMapping: Record<string, C2SArtifactFileType | ""> = {};
        unmatched.forEach((file) => {
          const lastDotIndex = file.name.lastIndexOf(".");
          const fileExtension = lastDotIndex === -1 ? "" : file.name.slice(lastDotIndex).toLowerCase();
          // Auto-select layersConfiguration for JSON files (only valid option)
          if (fileExtension === ".json") {
            initialMapping[file.name] = "layersConfiguration";
          } else {
            initialMapping[file.name] = "";
          }
        });
        setFileMapping(initialMapping);
        setShowMappingModal(true);
      }
    },
    [files, isReadOnly, change, manualMappings, versionId]
  );

  const renderFileStatus = useCallback(
    (ids: string[]) => {
      return c2sFileMap.ArtifactFiles.filter(({ id }) => ids.includes(id)).map(({ id, file, title }) => {
        let success = false;
        if (id === "costBreakdown") {
          success = !!scenarioConfigInput?.CostBreakdown?.Data;
        } else if (id === "bom") {
          success = !!scenarioConfigInput?.BillOfMaterials?.Data;
        } else if (id === "layersConfiguration") {
          success = !!layersConfiguration?.Data;
        } else if (id === "spliceTables") {
          success = (spliceTables?.length ?? 0) > 0 || hasExistingSpliceTables;
        }

        const isSpliceTablesLoadingRow = id === "spliceTables" && isSpliceTablesLoading;
        const hasError = fileErrors[id];
        const uploadedNames = uploadedFileNames[id] ?? [];
        const hasPending = uploadedNames.length > 0;
        const IconComponent = success && !hasError ? CheckCircle : CheckCircleOutline;
        const color = hasError ? "error" : success ? "success" : "action";

        let statusText = `(${file})`;
        if (isSpliceTablesLoadingRow) {
          statusText = "(checking for existing data…)";
        } else if (hasPending) {
          statusText = `(${formatUploadedNames(uploadedNames)})`;
        } else if (id === "spliceTables" && hasExistingSpliceTables) {
          statusText = "(provided)";
        }

        return (
          <TableRow key={title}>
            <TableCell sx={{ whiteSpace: "nowrap", width: "1%", pr: 0 }}>
              <Box display="flex" alignItems="center">
                {isSpliceTablesLoadingRow ? (
                  <CircularProgress size={16} sx={{ mr: 1 }} data-testid="splice-tables-loading" />
                ) : (
                  <IconComponent color={color} sx={{ mr: 1 }} />
                )}
                <Typography sx={{ fontWeight: 500 }} data-testid="file-title">
                  {title}
                </Typography>
              </Box>
            </TableCell>
            <TableCell align="left" sx={{ pl: 0.5 }}>
              <Box display="flex" alignItems="center" flexWrap="wrap" columnGap={2}>
                <Typography variant="body3" data-testid="file-extensions">
                  {statusText}
                </Typography>
                {hasError && (
                  <Typography variant="body3" color="error.main" data-testid="file-status">
                    {fileErrors[id]}
                  </Typography>
                )}
              </Box>
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap", width: "1%" }} />
          </TableRow>
        );
      });
    },
    [fileErrors, scenarioConfigInput, layersConfiguration, spliceTables, hasExistingSpliceTables, isSpliceTablesLoading, uploadedFileNames]
  );

  const handleManualMappingConfirm = useCallback(() => {
    // Create a map of existing files
    const existingFileMap = new Map(files.map((file) => [file.name, file]));

    // Save manual mappings and add files to the existing files
    const newMappings: Record<string, C2SArtifactFileType> = { ...manualMappings };

    unmatchedFiles.forEach((file) => {
      const mappedType = fileMapping[file.name];
      if (mappedType) {
        addFilesOfType(existingFileMap, mappedType, [file], manualMappings);
        newMappings[file.name] = mappedType;
      }
    });

    // Track manual mapping confirmation
    const mappingCount = Object.values(fileMapping).filter((type) => type !== "").length;
    analytics.track("collaboration.cts.files.manual_mapping.confirmed", {
      versionId,
      mappingCount,
    });

    // Update manual mappings state
    setManualMappings(newMappings);

    // Update form state with all files
    change("Files.Artifacts", Array.from(existingFileMap.values()));

    // Close the modal and clear temporary state
    setShowMappingModal(false);
    setUnmatchedFiles([]);
    setFileMapping({});
  }, [files, manualMappings, unmatchedFiles, fileMapping, versionId, change]);

  const handleManualMappingCancel = useCallback(() => {
    // Just close the modal without adding the unmatched files
    setShowMappingModal(false);
    setUnmatchedFiles([]);
    setFileMapping({});
  }, []);

  const handleFileMapChange = useCallback((fileName: string, mappingType: C2SArtifactFileType | "") => {
    setFileMapping((prev) => ({
      ...prev,
      [fileName]: mappingType,
    }));
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Artifacts
        </Typography>
        {!isReadOnly && (
          <Dropzone
            acceptedFiles={{
              "application/json": [".json"],
              "text/csv": [".csv"],
            }}
            message="Drag and drop your artifact files or click to browse."
            onDropAccepted={handleFilesChange}
            data-testid="artifacts-dropzone"
          />
        )}
        {visibleFileSections.map(({ key, title, ids }) => {
          const { error, touched: sectionTouched } = sectionErrors[key] ?? {};
          return (
            <Box key={key} sx={{ mt: 2 }}>
              <Table size="small" variant="ag-grid-standard">
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={3}>
                      {title}{" "}
                      {error && sectionTouched && (
                        <Typography color="error" component="span">
                          * {error}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{renderFileStatus(ids)}</TableBody>
              </Table>
            </Box>
          );
        })}
      </Box>

      {/* Manual File Mapping Modal */}
      <UploadFileMappingModal
        open={showMappingModal}
        unmatchedFiles={unmatchedFiles}
        fileMapping={fileMapping}
        onFileMapChange={handleFileMapChange}
        onConfirm={handleManualMappingConfirm}
        onCancel={handleManualMappingCancel}
      />
    </Box>
  );
};

export default UploadConfigurationStep;
